/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { UtrValueTypeMap, UtrValueTypes, isNumeric } from '../utils/universalTracker';
import { getUnitsForType, UnitTypes } from '../utils/units';
import { standards, frameworks } from '@g17eco/core';
import { getName } from '../utils/universalTracker';
import { Tags, UniversalTrackerPlain, UtrValueType, ValueValidation } from '../types/universalTracker';
import { Option } from '../slice/valueListSlice';
import { ValueDataData } from '../components/survey/question/questionInterfaces';
import { SerializedEditorState } from 'lexical';

export enum UniversalTrackerType {
  /* @deprecated */
  Frameworks = 'frameworks',
  Utr = 'utr',
}

export type UniversalTrackerBase = Pick<
  UniversalTrackerPlain,
  | '_id'
  | 'code'
  | 'typeCode'
  | 'name'
  | 'description'
  | 'alternatives'
  | 'type'
  | 'valueLabel'
  | 'numberScale'
  | 'tags'
  | 'unit'
  | 'instructions'
  | 'instructionsEditorState'
  | 'unitType'
  | 'valueValidation'
  | 'typeTags'
  | 'targetDirection'
  | 'numberScaleInput'
  | 'instructionsLink'
  | 'evidenceInstructions'
  | 'valueType'
> & {
  created?: string;
};

type AlternativeProps = Pick<UniversalTrackerBase, 'name' | 'description' | 'valueLabel' | 'instructions' | 'evidenceInstructions' | 'instructionsLink' | 'typeCode'>;

export default class UniversalTracker {
  constructor(
    private universalTracker: UniversalTrackerBase,
    private type: UniversalTrackerType = UniversalTrackerType.Utr
  ) {
    this.universalTracker = universalTracker;
    this.type = type;
  }

  private mergeTableValidation(valueValidation: ValueValidation, initiativeUtrValueValidation: ValueValidation) {
    if (!initiativeUtrValueValidation?.table || !valueValidation.table) {
      return {};
    }

    const mergedColumns = valueValidation.table.columns.map((col) => {
      const overriddenValidation = initiativeUtrValueValidation.table?.columns.find(
        (c) => c.code === col.code
      )?.validation;
      return { ...col, validation: { ...col.validation, decimal: overriddenValidation?.decimal } };
    });

    return { table: { ...valueValidation.table, columns: mergedColumns } };
  }

  public overrideValueValidation(initiativeUtrValueValidation: ValueValidation) {
    const valueValidation = this.getValueValidation();
    const tableValueValidation = this.mergeTableValidation(valueValidation, initiativeUtrValueValidation);
    this.universalTracker.valueValidation = {
      ...valueValidation,
      ...initiativeUtrValueValidation,
      ...tableValueValidation,
    };
  }

  public getRaw<T extends UniversalTrackerBase = UniversalTrackerPlain>(): T {
    return this.universalTracker as T;
  }

  public getId() {
    return this.universalTracker._id;
  }

  public getCode() {
    return this.universalTracker.code;
  }

  public isFramework() {
    return this.type === UniversalTrackerType.Frameworks;
  }

  private getAlternativeWithFallback(
    attribute: keyof AlternativeProps,
    alternativeCode: string,
    secondaryFallback: string
  ): string;

  private getAlternativeWithFallback(
    attribute: 'instructionsEditorState',
    alternativeCode: string,
    secondaryFallback: undefined
  ): SerializedEditorState | undefined;

  private getAlternativeWithFallback(
    attribute: keyof AlternativeProps | 'instructionsEditorState',
    alternativeCode: string,
    secondaryFallback: string | undefined,
  ): string | SerializedEditorState | undefined {
    if (attribute === 'instructionsEditorState') {
      return alternativeCode === this.getType()
        ? this.universalTracker[attribute]
        : this.universalTracker.alternatives?.[alternativeCode]?.[attribute];
    }
    if (alternativeCode === this.getType()) {
      return this.universalTracker[attribute] || secondaryFallback;
    }
    if (alternativeCode && this.hasAlternativeInfo(alternativeCode) && 'alternatives' in this.universalTracker) {
      return this.universalTracker.alternatives?.[alternativeCode]?.[attribute] || secondaryFallback;
    }
    return this.universalTracker[attribute] || secondaryFallback;
  }

  public getShortPrefix(alternativeCode = '') {
    const type = standards[alternativeCode]?.shortName || '';
    const code = this.getTypeCode(alternativeCode);
    if (type) {
      return `${type} ${code}`;
    }
    return code;
  }

  /** @deprecated not used? **/
  public getNameWithPrefix(alternativeCode = '') {
    const code = alternativeCode || this.getType();
    return `${this.getShortPrefix(code)} ${this.getName(code)}`;
  }

  public getName(alternativeCode = '') {
    return this.getAlternativeWithFallback('name', alternativeCode, '');
  }

  public getBestName(alternativeCodes?: string[]): string {
    return getName(this.universalTracker as UniversalTrackerPlain, alternativeCodes);
  }

  /** @deprecated not used? **/
  public getDescription(alternativeCode = '') {
    return this.getAlternativeWithFallback('description', alternativeCode, '');
  }

  public getValueLabel(alternativeCode = '') {
    return this.getAlternativeWithFallback('valueLabel', alternativeCode, 'Value');
  }

  public getInstructions(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructions', alternativeCode, '');
  }

  public getInstructionsEditorState(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructionsEditorState', alternativeCode, undefined);
  }

  public getEvidenceInstructions(alternativeCode = '') {
    return this.getAlternativeWithFallback('evidenceInstructions', alternativeCode, '');
  }

  public getInstructionsLink(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructionsLink', alternativeCode, '');
  }

  public hasAlternativeInfo(alternativeCode = '') {
    if (alternativeCode === this.getType()) {
      // Root level data should be the correct one, so no need to check alternatives
      return true;
    }

    return !!this.universalTracker.alternatives?.[alternativeCode];
  }

  private getAlternatives() {
    const alternatives = [this.getType()];

    if (!this.universalTracker.alternatives) {
      return alternatives;
    }

    const validStandards = Object.keys(standards);
    const alternativeKeys = Object.keys(this.universalTracker.alternatives);
    alternativeKeys.forEach((alternativeCode) => {
      if (validStandards.includes(alternativeCode)) {
        alternatives.push(alternativeCode);
      }
    });
    return alternatives;
  }

  public getAlternativeLanguages() {
    if (!this.universalTracker.alternatives) {
      return [];
    }

    const alternativeKeys = Object.keys(this.universalTracker.alternatives);
    return alternativeKeys.filter(
      (alternativeCode) => this.universalTracker.alternatives?.[alternativeCode].alternativeType === 'language'
    );
  }

  /** @deprecated not used? **/
  public getAlternativesCount() {
    return this.getAlternatives().length;
  }

  public getTargetDirection() {
    return this.universalTracker.targetDirection ?? '';
  }

  public getType() {
    return this.universalTracker.type;
  }

  public getTypeTags() {
    return this.universalTracker.typeTags ?? [];
  }

  public getAltTypeTags() {
    if (!this.universalTracker.alternatives) {
      return [];
    }

    const typeTags: string[] = [];
    Object.values(this.universalTracker.alternatives).forEach((alt) => {
      if (Array.isArray(alt.typeTags)) {
        typeTags.push(...alt.typeTags);
      }
    });
    return typeTags;
  }

  public getTypeName(alternativeCode = '') {
    const type = this.hasAlternativeInfo(alternativeCode) ? alternativeCode : this.getType();

    if (type === 'kpi') {
      return 'Standardized Metric';
    }
    if (type === 'custom_kpi') {
      return 'Custom Metric';
    }
    return standards[type]?.name || frameworks[type]?.name || '';
  }

  /** @deprecated not used? **/
  public isCustomMetric() {
    return this.universalTracker.type === 'custom_kpi';
  }

  public getFrameworkCode() {
    return `${this.getType()}/${this.getTypeCode()}`;
  }

  public getTypeCode(alternativeCode = '') {
    return this.getAlternativeWithFallback('typeCode', alternativeCode, '');
  }

  public getSuffix() {
    return this.isPercentage() ? '%' : '';
  }

  public getMetricUnitCode() {
    return this.universalTracker.unit;
  }

  public getNumberScale() {
    return this.universalTracker.numberScale;
  }

  public getUnitOptions() {
    return getUnitsForType(this.universalTracker.unitType);
  }

  public isCurrency() {
    return this.universalTracker.unitType === UnitTypes.currency;
  }

  public getValueType() {
    return this.universalTracker.valueType as UtrValueType;
  }

  public getValueTypeName() {
    return UtrValueTypeMap[this.universalTracker.valueType as UtrValueType] ?? '';
  }

  public isType(type: string) {
    return this.universalTracker.valueType === type;
  }

  public getValueListOptions() {
    if (
      ![
        UtrValueTypes.valueList,
        UtrValueTypes.valueListMulti,
        UtrValueTypes.numericValueList,
        UtrValueTypes.textValueList,
      ].includes(this.getValueType()) ||
      !this.universalTracker.valueValidation ||
      !this.universalTracker.valueValidation.valueList
    ) {
      return [];
    }

    let list: Option[] | undefined;
    const valueList = this.universalTracker.valueValidation.valueList;
    switch (valueList.type) {
      case 'list':
        list = valueList.list;
        break;
      case 'custom':
        list = valueList.custom;
        break;
      default:
        break;
    }

    if (Array.isArray(list)) {
      return list;
    }
    return [];
  }

  private getValueListOptionByCode(code: string) {
    const options = this.getValueListOptions();
    const option = options.find((option) => option.code === code);
    if (option === undefined) {
      return code;
    }
    return option.name;
  }

  public getTags(group: keyof Tags) {
    return this.universalTracker.tags?.[group] ?? [];
  }
  public hasTag(group: string, tag: string) {
    return this.universalTracker.tags?.[group as keyof Tags]?.includes(tag);
  }
  public hasSubTag(tag?: string) {
    if (!this.universalTracker.tags) {
      return false;
    }
    // If its root tag, only include non-empty array,
    // as keys added by schema with default empty [] as value
    if (Number(this.universalTracker.tags[tag as keyof Tags]?.length) > 0) {
      return true;
    }
    return Object.values(this.universalTracker.tags).flat().includes(tag);
  }

  /** @deprecated not used? **/
  public getTagsCount() {
    if (!this.universalTracker.tags) {
      return 0;
    }
    let count = 0;
    Object.values(frameworks).forEach((framework) => {
      if (framework.subgroups) {
        framework.subgroups.forEach((f) => {
          if (this.hasTag(framework.code as keyof Tags, f.code as keyof Tags)) {
            count = count + 1;
          }
        });
      }
    });
    return count;
  }

  public hasMinAndMax() {
    if (this.getMin() === undefined || this.getMax() === undefined) {
      return false;
    }
    return true;
  }

  public getValueValidation() {
    if (!this.universalTracker.valueValidation) {
      return {};
    }

    return this.universalTracker.valueValidation;
  }

  public getMin(fallback = undefined) {
    if (!this.universalTracker.valueValidation) {
      return fallback;
    }
    if (typeof this.universalTracker.valueValidation.min !== 'number') {
      return fallback;
    }
    return this.universalTracker.valueValidation.min;
  }

  public getMax(fallback = undefined) {
    if (!this.universalTracker.valueValidation) {
      return fallback;
    }
    if (typeof this.universalTracker.valueValidation.max !== 'number') {
      return fallback;
    }
    return this.universalTracker.valueValidation.max;
  }

  public getValueListMultiData(data?: ValueDataData) {
    if (Array.isArray(data)) {
      return data.map((code) => this.getValueListOptionByCode(code));
    }
    return [];
  }

  public getValueListData(data?: ValueDataData) {
    if (typeof data === 'string') {
      return this.getValueListOptionByCode(data);
    }
    return '';
  }

  public isPercentage() {
    return this.getValueType() === 'percentage';
  }

  /** @deprecated not used? **/
  public isMultipleChoice() {
    return this.universalTracker.valueType === 'valueListMulti';
  }

  public hasValueData() {
    switch (this.universalTracker.valueType) {
      case UtrValueType.Text:
      case UtrValueType.Date:
      case UtrValueType.ValueList:
      case UtrValueType.ValueListMulti:
      case UtrValueType.NumericValueList:
      case UtrValueType.TextValueList:
      case UtrValueType.Table:
        return true;
      case UtrValueType.Sample:
      case UtrValueType.Number:
      case UtrValueType.Percentage:
      default:
        return false;
    }
  }

  /** @deprecated not used? **/
  public isValueDataAnObject() {
    if (!this.hasValueData()) {
      return false;
    }

    switch (this.universalTracker.valueType) {
      case UtrValueType.NumericValueList:
      case UtrValueType.TextValueList:
        return true;
      case UtrValueType.ValueList:
      case UtrValueType.ValueListMulti:
      case UtrValueType.Sample:
      case UtrValueType.Number:
      case UtrValueType.Percentage:
      case UtrValueType.Text:
      case UtrValueType.Date:
      default:
        return false;
    }
  }

  isNumeric() {
    return isNumeric(this.universalTracker);
  }

  /** @deprecated not used? **/
  requiresSampleSize() {
    if (this.isFramework()) {
      return false;
    }

    return this.universalTracker.valueType === UtrValueType.Sample;
  }
}
